import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, Bell, ArrowLeft, MessageSquare, Phone, Mail, HelpCircle, AlertCircle, Shield } from 'lucide-react';

const SMSTermsPage = () => {
  const lastUpdated = "March 20, 2026";

  return (
    <div className="min-h-screen bg-paper">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-ink-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <CalendarCheck className="w-7 h-7 text-ink" />
              <span className="text-xl font-bold text-ink font-display">Enota</span>
            </Link>
            <Link to="/" className="text-ink-600 hover:text-ink flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-md border border-ink-200 shadow-sm p-8 md:p-12">
            <div className="flex items-center gap-3 mb-4">
              <MessageSquare className="w-8 h-8 text-respondent" />
              <h1 className="text-3xl sm:text-4xl font-bold text-ink font-display">
                SMS Terms
              </h1>
            </div>
            <p className="text-ink-600 mb-8">Last Updated: {lastUpdated}</p>

            <p className="text-ink-600 leading-relaxed mb-8">
              Terms and conditions for SMS messaging services provided by Enota, a service owned and 
              operated by <strong>OFFER GADGET LLC</strong>.
            </p>

            {/* Program Info Cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              <div className="bg-paper rounded-md p-4 border border-ink-200">
                <p className="text-xs text-ink-600 uppercase tracking-wide mb-1">Short Code</p>
                <p className="text-lg font-bold text-ink">RENWL (TBD)</p>
              </div>
              <div className="bg-paper rounded-md p-4 border border-ink-200">
                <p className="text-xs text-ink-600 uppercase tracking-wide mb-1">Program</p>
                <p className="text-lg font-bold text-ink">Enota</p>
              </div>
              <div className="bg-paper rounded-md p-4 border border-ink-200">
                <p className="text-xs text-ink-600 uppercase tracking-wide mb-1">Frequency</p>
                <p className="text-lg font-bold text-ink">Up to 4/month</p>
              </div>
              <div className="bg-paper rounded-md p-4 border border-ink-200">
                <p className="text-xs text-ink-600 uppercase tracking-wide mb-1">Pricing</p>
                <p className="text-lg font-bold text-ink">Msg & Data rates may apply</p>
              </div>
            </div>

            <div className="prose prose-slate max-w-none">
              {/* User Opt-In */}
              <h2 className="text-xl font-bold text-ink font-display mt-8 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-respondent" />
                User Opt-In
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                By signing up at{' '}
                <Link to="/register" className="text-respondent hover:text-respondent underline">
                  https://enota.ai/register
                </Link>{' '}
                and opting in to SMS notifications, you consent to receive SMS messages from Enota, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>Renewal reminder notifications</li>
                <li>Account-related alerts and status updates</li>
                <li>Service reminders and updates</li>
                <li>Occasional promotional messages about new features</li>
              </ul>
              <div className="bg-cream border border-ink-200 rounded-md p-4 mb-6">
                <p className="text-respondent text-sm">
                  <strong>Important:</strong> Messages are <strong>not</strong> sent using an automatic telephone 
                  dialing system. Consent to receive SMS messages is <strong>not</strong> a condition of purchase 
                  or use of the Enota Service.
                </p>
              </div>
              <p className="text-ink-600 leading-relaxed mb-4">
                All information collected during the SMS opt-in process will <strong>not</strong> be sold, 
                rented, or shared with third parties for marketing purposes.
              </p>

              {/* User Opt-Out */}
              <h2 className="text-xl font-bold text-ink font-display mt-10 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                User Opt-Out
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                To stop receiving SMS messages at any time:
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-6 mb-4">
                <p className="text-amber-900 font-semibold text-lg mb-2">Text "STOP" to RENWL (TBD)</p>
                <p className="text-amber-700 text-sm">
                  You may also reply with "QUIT," "END," "CANCEL," "UNSUBSCRIBE," or "STOP ALL."
                </p>
              </div>
              <p className="text-ink-600 leading-relaxed mb-4">
                Once you opt out, you will no longer receive SMS messages from Enota. You may also 
                disable SMS notifications in your{' '}
                <Link to="/settings" className="text-respondent hover:text-respondent underline">account settings</Link>.
              </p>

              {/* Help & Support */}
              <h2 className="text-xl font-bold text-ink font-display mt-10 mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-emerald-600" />
                Help & Support
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                For assistance with SMS messaging:
              </p>
              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-center">
                  <p className="text-xs text-emerald-600 uppercase tracking-wide mb-2">Text Help</p>
                  <p className="text-emerald-900 font-bold">HELP to TBD</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-center">
                  <p className="text-xs text-emerald-600 uppercase tracking-wide mb-2">Email</p>
                  <a href="mailto:support@renewalnote.com" className="text-emerald-900 font-bold hover:underline">
                    support@renewalnote.com
                  </a>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-center">
                  <p className="text-xs text-emerald-600 uppercase tracking-wide mb-2">Phone</p>
                  <p className="text-emerald-900 font-bold">(877) 739-2487</p>
                </div>
              </div>

              {/* User Fees */}
              <h2 className="text-xl font-bold text-ink font-display mt-10 mb-4">
                User Fees
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                This is a standard-rate messaging service. <strong>Message and data rates may apply</strong> based 
                on your mobile carrier's plan. Enota does not charge any additional fees for SMS messages 
                beyond what your carrier may charge.
              </p>

              {/* Supported Carriers */}
              <h2 className="text-xl font-bold text-ink font-display mt-10 mb-4">
                Supported Carriers (U.S. Only)
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                Our SMS service is supported by most major U.S. carriers, including but not limited to:
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {['AT&T', 'T-Mobile', 'Verizon Wireless', 'Sprint', 'Boost Mobile', 'MetroPCS', 'U.S. Cellular', 'Cricket', 'Virgin Mobile', 'Straight Talk'].map((carrier) => (
                  <span 
                    key={carrier} 
                    className="px-3 py-1 bg-cream text-ink rounded-full text-sm font-medium"
                  >
                    {carrier}
                  </span>
                ))}
              </div>
              <p className="text-ink-600 text-sm mb-4">
                Carriers are not responsible for delayed or undelivered messages.
              </p>

              {/* Message Types */}
              <h2 className="text-xl font-bold text-ink font-display mt-10 mb-4">
                Types of Messages
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                When you opt in to SMS notifications, you may receive the following types of messages:
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm border border-ink-200 rounded-md">
                  <thead className="bg-paper">
                    <tr>
                      <th className="text-left py-3 px-4 text-ink font-semibold border-b border-ink-200">Message Type</th>
                      <th className="text-left py-3 px-4 text-ink font-semibold border-b border-ink-200">Description</th>
                      <th className="text-left py-3 px-4 text-ink font-semibold border-b border-ink-200">Frequency</th>
                    </tr>
                  </thead>
                  <tbody className="text-ink-600">
                    <tr className="border-b border-ink-200">
                      <td className="py-3 px-4 font-medium">Renewal Reminders</td>
                      <td className="py-3 px-4">Notifications about upcoming renewal deadlines</td>
                      <td className="py-3 px-4">Based on your settings (7, 3, 1 days before)</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-3 px-4 font-medium">Account Alerts</td>
                      <td className="py-3 px-4">Security alerts, password changes, payment confirmations</td>
                      <td className="py-3 px-4">As needed</td>
                    </tr>
                    <tr className="border-b border-ink-200">
                      <td className="py-3 px-4 font-medium">Service Updates</td>
                      <td className="py-3 px-4">Important service announcements and changes</td>
                      <td className="py-3 px-4">Occasional</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-medium">Promotional</td>
                      <td className="py-3 px-4">New features, special offers (optional)</td>
                      <td className="py-3 px-4">Up to 2/month</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Warranty Disclaimer */}
              <h2 className="text-xl font-bold text-ink font-display mt-10 mb-4">
                Warranty Disclaimer
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                Enota is not responsible for any delays in SMS delivery, as delivery depends on your 
                wireless carrier's network. We make reasonable efforts to deliver messages promptly, but 
                cannot guarantee delivery times or delivery at all due to factors beyond our control.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                <strong>You acknowledge and agree that SMS reminders are a convenience feature and should 
                not be your sole method of tracking renewal deadlines.</strong> We recommend also checking 
                your dashboard and email for important renewal notifications.
              </p>

              {/* Privacy */}
              <h2 className="text-xl font-bold text-ink font-display mt-10 mb-4">
                Privacy Policy
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                Your privacy is important to us. The phone number and information you provide for SMS 
                services is used <strong>only</strong> to deliver requested communications and manage 
                your account.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                We will not sell, rent, or share your phone number with third parties for marketing purposes. 
                We may disclose information only when legally required (e.g., by court order) or when necessary 
                to protect our rights or property.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                For more information, please review our full{' '}
                <Link to="/privacy" className="text-respondent hover:text-respondent underline">Privacy Policy</Link>.
              </p>

              {/* Dispute Resolution */}
              <h2 className="text-xl font-bold text-ink font-display mt-10 mb-4">
                Dispute Resolution
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                All disputes arising from these SMS Terms will be resolved through <strong>binding arbitration</strong> in 
                accordance with the American Arbitration Association's rules, as described in our{' '}
                <Link to="/terms" className="text-respondent hover:text-respondent underline">Terms of Use</Link>.
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-ink-600">
                <li>Arbitration will be conducted by a single arbitrator, whose decision is final and binding</li>
                <li>Both parties waive the right to participate in any class action or jury trial</li>
                <li>Each party will bear their own costs unless the arbitrator awards fees to the prevailing party</li>
              </ul>

              {/* Miscellaneous */}
              <h2 className="text-xl font-bold text-ink font-display mt-10 mb-4">
                Miscellaneous
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                These SMS Terms, together with our{' '}
                <Link to="/terms" className="text-respondent hover:text-respondent underline">Terms of Use</Link> and{' '}
                <Link to="/privacy" className="text-respondent hover:text-respondent underline">Privacy Policy</Link>, 
                form the entire agreement between you and Enota regarding SMS communications.
              </p>
              <p className="text-ink-600 leading-relaxed mb-4">
                Updates or modifications to these SMS Terms will be posted at{' '}
                <span className="text-ink font-medium">https://enota.ai/sms-terms</span> and are 
                effective immediately unless otherwise noted. Your continued use of SMS services constitutes 
                acceptance of any updated Terms.
              </p>

              {/* Contact */}
              <h2 className="text-xl font-bold text-ink font-display mt-10 mb-4">
                Contact Us
              </h2>
              <p className="text-ink-600 leading-relaxed mb-4">
                For any questions about these SMS Terms:
              </p>
              <div className="bg-paper p-4 rounded-md border border-ink-200">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-ink-600" />
                  <span className="text-ink">support@renewalnote.com</span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4 text-ink-600" />
                  <span className="text-ink">(877) 739-2487</span>
                </div>
                <div className="flex items-start gap-2">
                  <Bell className="w-4 h-4 text-ink-600 mt-0.5" />
                  <span className="text-ink"><strong>OFFER GADGET LLC</strong><br />53 Calle Palmeras Ste 601<br />San Juan, PR 00901</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-ink text-ink-600/70 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <CalendarCheck className="w-6 h-6 text-white" />
              <span className="text-lg font-bold text-white font-display">Enota</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/california-privacy" className="hover:text-white transition-colors">California Residents</Link>
              <Link to="/sms-terms" className="hover:text-white transition-colors">SMS Terms</Link>
              <Link to="/login" className="hover:text-white transition-colors">Login</Link>
            </div>
            <p className="text-sm">&copy; 2026 Enota. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SMSTermsPage;
